import { useState } from 'react';
import { TechnischeRessource, SRType } from '../../../types/network';
import { PlusIcon, TrashIcon, ChevronIcon } from '../../icons/ButtonIcons';
import { PvGenerationDisplay } from './PvGenerationDisplay';
import { useViewMode } from '../../../context/useViewMode';
import { fmt } from '../../../utils/numberFormat';
import { useReadOnlyUi } from '../../../store/featureStore';
import { formatCalendarDate, formatWattAsKw, isSynced, participationLabel } from './controlMasterDataLabels';
import { Fact, SourceBadge, TakeoverHint } from './ControlMasterDataParts';

interface TechnischeRessourceSectionProps {
  technischeRessourcen: TechnischeRessource[];
  elementId: string;
  onUpdate: (technischeRessourcen: TechnischeRessource[]) => Promise<void>;
}

const srTypeOptions: { value: SRType; label: string }[] = [
  { value: 'pv_anlage', label: 'PV Anlage' },
  { value: 'battery_storage', label: 'Batteriespeicher' },
  { value: 'heat_pump', label: 'Wärmepumpe' },
  { value: 'wallbox', label: 'Wallbox' },
  { value: 'air_conditioning', label: 'Klimaanlage' },
  { value: 'other', label: 'Sonstige steuerbare Einrichtung' },
];

const inputClass = 'w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-white';
const labelClass = 'block text-[11px] text-gray-500 dark:text-primary-400 mb-0.5';

/** § 14a flag as select value: 'yes' | 'no' | empty (unknown - the type rule applies). */
const participationValue = (v?: boolean): string => (v === true ? 'yes' : v === false ? 'no' : '');

const RESOURCE_TYPE_CONFIG: Record<SRType, { icon: string; bg: string; border: string }> = {
  'pv_anlage':        { icon: '\u2600\uFE0F', bg: 'bg-sky-400 dark:bg-sky-500',           border: 'border-sky-500 dark:border-sky-600' },
  'battery_storage':  { icon: '\uD83D\uDD0B',  bg: 'bg-gray-400 dark:bg-gray-500',         border: 'border-gray-500 dark:border-gray-600' },
  'heat_pump':        { icon: '\uD83C\uDF21\uFE0F', bg: 'bg-orange-300 dark:bg-orange-400',     border: 'border-orange-400 dark:border-orange-500' },
  'wallbox':          { icon: '\uD83D\uDD0C',  bg: 'bg-violet-300 dark:bg-violet-400',     border: 'border-violet-400 dark:border-violet-500' },
  'air_conditioning': { icon: '\u2744\uFE0F', bg: 'bg-slate-400 dark:bg-slate-500',       border: 'border-slate-500 dark:border-slate-600' },
  'other':            { icon: '\u2699\uFE0F', bg: 'bg-zinc-300 dark:bg-zinc-500',         border: 'border-zinc-400 dark:border-zinc-600' },
};

const TypeIcon = ({ type }: { type: SRType }) => {
  const config = RESOURCE_TYPE_CONFIG[type] ?? RESOURCE_TYPE_CONFIG.other;
  return (
    <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center text-sm shrink-0 shadow-sm`}>
      {config.icon}
    </div>
  );
};

/** Builds the compact specs line for view mode */
const buildSpecsLine = (sr: TechnischeRessource): string[] => {
  const specs: string[] = [];
  if (sr.type === 'pv_anlage') {
    if (sr.maxPower > 0) specs.push(`${fmt(sr.maxPower, 1)} kWp`);
  } else {
    if (sr.maxPower > 0) specs.push(`${fmt(sr.maxPower, 1)} kW`);
  }
  if (sr.type === 'battery_storage' && sr.storageCapacityKwh !== undefined) {
    specs.push(`${fmt(sr.storageCapacityKwh, 1)} kWh`);
  }
  if (sr.type === 'heat_pump' && sr.auxiliaryHeaterPowerKw !== undefined && sr.auxiliaryHeaterPowerKw > 0) {
    specs.push(`Zusatzheizung ${fmt(sr.auxiliaryHeaterPowerKw, 1)} kW`);
  }
  return specs;
};

export const TechnischeRessourceSection = ({
  technischeRessourcen,
  elementId,
  onUpdate,
}: TechnischeRessourceSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Read-only deployments: no add/edit/delete controls.
  const readOnly = useReadOnlyUi();

  // Stresstest-Modus und Parameter
  const viewMode = useViewMode();
  const isStressTestMode = viewMode === 'stress-test';

  // Jede Ressource nimmt am Planungslauf teil (beobachteter Frame bzw. Nenn-Delta).
  const isResourceActiveInStressTest = (_type: SRType): boolean => true;

  const addTR = async () => {
    const newTR: TechnischeRessource = {
      id: crypto.randomUUID(),
      type: 'pv_anlage',
      maxPower: 0,
    };
    const updatedTRs = [...technischeRessourcen, newTR];
    await onUpdate(updatedTRs);
    setIsExpanded(true); // Auto-expand when adding new item
  };

  /**
   * Operator edits of a synced resource take it over into manual care (source = manual), otherwise the
   * next master-data sync would overwrite them. Computed values (PV generation) are not operator edits.
   */
  const updateSR = async (index: number, updates: Partial<TechnischeRessource>, takeOver = true) => {
    const updatedTRs = [...technischeRessourcen];
    const current = updatedTRs[index];
    updatedTRs[index] = { ...current, ...updates, ...(takeOver && isSynced(current.source) ? { source: 'manual' as const } : {}) };
    await onUpdate(updatedTRs);
  };

  const removeSR = async (index: number) => {
    const updatedTRs = technischeRessourcen.filter((_: TechnischeRessource, i: number) => i !== index);
    await onUpdate(updatedTRs);
  };

  const renderCardHeader = (sr: TechnischeRessource, index: number, typeLabel: string, isActive: boolean) => {
    return (
      <div className="flex items-center gap-3">
        <TypeIcon type={sr.type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-gray-900 dark:text-white">{typeLabel}</span>
              <SourceBadge source={sr.source} />
              {sr.type !== 'pv_anlage' && (sr.participates14a != null || isSynced(sr.source)) && (
                <span
                  className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                    sr.participates14a === true
                      ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-primary-600 dark:text-primary-200'
                  }`}
                  title="Angemeldete steuerbare Verbrauchseinrichtung nach § 14a EnWG (Stammdaten)"
                >
                  {participationLabel(sr)}
                </span>
              )}
              {isStressTestMode && (sr.type === 'pv_anlage' || sr.type === 'wallbox' || sr.type === 'heat_pump') && (
                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                  isActive
                    ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                    : 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300'
                }`}>
                  {isActive ? 'Aktiv' : 'Deaktiviert'}
                </span>
              )}
            </div>
            {!readOnly && (
            <div className="flex items-center gap-0.5">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setEditingId(sr.id);
                }}
                className="text-gray-400 dark:text-primary-400 p-1 rounded shrink-0 hover:bg-gray-200 dark:hover:bg-primary-500 hover:text-gray-600 dark:hover:text-primary-200 transition-colors"
                title="Bearbeiten"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  removeSR(index).catch(console.error);
                }}
                className="text-gray-400 dark:text-primary-400 p-1 rounded shrink-0 hover:bg-error-100 dark:hover:bg-error-900/20 hover:text-error-600 dark:hover:text-error-400 transition-colors"
                title="Löschen"
              >
                <TrashIcon />
              </button>
            </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEditFields = (sr: TechnischeRessource, index: number) => {
    return (
      <div className="space-y-2 mt-1">
        {/* Typ-Wechsler */}
        <select
          value={sr.type}
          onChange={(e) => {
            e.preventDefault();
            updateSR(index, { type: e.target.value as SRType }).catch(console.error);
          }}
          className="w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-white"
          aria-label="SR Type"
          title="SR Type"
        >
          {srTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <TakeoverHint source={sr.source} />

        {/* Maximale Leistung */}
        <div>
          <label className="block text-[11px] text-gray-500 dark:text-primary-400 mb-0.5">
            Max. Leistung ({sr.type === 'pv_anlage' ? 'kWp' : 'kW'})
          </label>
          <input
            type="number"
            value={sr.maxPower}
            onChange={(e) => {
              const value = parseFloat(e.target.value) || 0;
              updateSR(index, { maxPower: value }).catch(console.error);
            }}
            className="w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-white"
            min="0"
            step="0.1"
            aria-label="Maximale Leistung"
          />
        </div>

        {/* Battery storage specific - editable */}
        {sr.type === 'battery_storage' && (
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-primary-400 mb-0.5">
              Speicherkapazität (kWh)
            </label>
            <input
              type="number"
              value={sr.storageCapacityKwh ?? 0}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                updateSR(index, { storageCapacityKwh: value }).catch(console.error);
              }}
              className="w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-white"
              min="0"
              step="0.1"
              aria-label="Speicherkapazität"
            />
          </div>
        )}

        {/* Heat pump specific - editable */}
        {sr.type === 'heat_pump' && (
          <div>
            <label className="block text-[11px] text-gray-500 dark:text-primary-400 mb-0.5">
              Zusatzheizung (kW)
            </label>
            <input
              type="number"
              value={sr.auxiliaryHeaterPowerKw ?? 0}
              onChange={(e) => {
                const value = parseFloat(e.target.value) || 0;
                updateSR(index, { auxiliaryHeaterPowerKw: value }).catch(console.error);
              }}
              className="w-full px-2 py-1 text-[13px] border border-gray-300 dark:border-primary-500 rounded bg-white dark:bg-primary-700 text-gray-900 dark:text-white"
              min="0"
              step="0.1"
              aria-label="Zusatzheizung"
            />
          </div>
        )}

        {/* § 14a master data (migration 0060) - not for PV (§ 9 EEG, not a SteuVE) */}
        {sr.type !== 'pv_anlage' && (
          <div className="grid grid-cols-2 gap-2 pt-1 border-t border-gray-200 dark:border-primary-600">
            <div>
              <label className={labelClass}>§ 14a angemeldet</label>
              <select
                value={participationValue(sr.participates14a)}
                onChange={(e) => {
                  const v = e.target.value;
                  updateSR(index, { participates14a: v === 'yes' ? true : v === 'no' ? false : undefined }).catch(console.error);
                }}
                className={inputClass}
                aria-label="§ 14a angemeldet"
              >
                <option value="">unbekannt (Gerätetyp entscheidet)</option>
                <option value="yes">ja</option>
                <option value="no">nein</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Regelung (Ziff. 10)</label>
              <select
                value={sr.regime ?? 'neu'}
                onChange={(e) => updateSR(index, { regime: e.target.value as TechnischeRessource['regime'] }).catch(console.error)}
                className={inputClass}
                aria-label="Regelung nach BK6-22-300"
                title="Nur Anlagen der neuen Regelung steuert GridPilot; Altanlagen laufen bis zum Wechsel (längstens Ende 2028) nach der bisherigen Regelung"
              >
                <option value="neu">neue Regelung (GridPilot steuert)</option>
                <option value="alt_14a_bis_2028">alte § 14a-Vereinbarung (bis Wechsel, längstens 2028)</option>
                <option value="nachtspeicher_alt">Nachtspeicherheizung (alte Regelung)</option>
                <option value="nicht_steuerbar_10_6">nachweislich nicht steuerbar (Ziff. 10.6)</option>
              </select>
            </div>
            <div>
              <label className={labelClass}>Mindestleistung Anlage (kW)</label>
              <input
                type="number"
                value={sr.deviceMinPowerW != null ? sr.deviceMinPowerW / 1000 : ''}
                onChange={(e) => {
                  const kw = parseFloat(e.target.value);
                  updateSR(index, { deviceMinPowerW: Number.isFinite(kw) && kw >= 0 ? Math.round(kw * 1000) : undefined }).catch(console.error);
                }}
                placeholder="z. B. 4,2"
                className={inputClass}
                min="0"
                step="0.1"
                aria-label="Mindestleistung der Anlage"
                title="Vom Installateur eingestellte Mindestleistung - die tatsächliche Wirkung eines Relaisbefehls"
              />
            </div>
            <div>
              <label className={labelClass}>TR-ID</label>
              <input
                type="text"
                value={sr.trId ?? ''}
                onChange={(e) => updateSR(index, { trId: e.target.value.trim() || undefined }).catch(console.error)}
                placeholder="D…"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div>
              <label className={labelClass}>SR-ID</label>
              <input
                type="text"
                value={sr.srId ?? ''}
                onChange={(e) => updateSR(index, { srId: e.target.value.trim() || undefined }).catch(console.error)}
                placeholder="C…"
                className={`${inputClass} font-mono`}
              />
            </div>
            <div className="col-span-2">
              <label className={labelClass}>NeLo-ID</label>
              <input
                type="text"
                value={sr.neloId ?? ''}
                onChange={(e) => updateSR(index, { neloId: e.target.value.trim() || undefined }).catch(console.error)}
                placeholder="E…"
                className={`${inputClass} font-mono`}
              />
            </div>
          </div>
        )}

        <button
          onClick={(e) => {
            e.preventDefault();
            setEditingId(null);
          }}
          className="w-full px-2 py-1 text-[12px] bg-gray-200 dark:bg-primary-500 text-gray-700 dark:text-primary-200 rounded hover:bg-gray-300 dark:hover:bg-primary-400 transition-colors"
        >
          Fertig
        </button>
      </div>
    );
  };

  return (
    <div className="relative bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-400 dark:bg-emerald-500" />
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full text-left pl-4 pr-3 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-700/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="shrink-0 w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-200">Technische Ressourcen</span>
          {technischeRessourcen.length > 0 && (
            <span className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">{technischeRessourcen.length}</span>
          )}
        </div>
        <ChevronIcon
          direction={isExpanded ? "up" : "down"}
          className="w-4 h-4 text-gray-400 dark:text-gray-500"
        />
      </button>

      {isExpanded && (
        <div className="pl-4 pr-3 pb-3">
          <div className="space-y-2 mb-2">
            {technischeRessourcen.map((sr: TechnischeRessource, index: number) => {
              const isEditing = editingId === sr.id;
              const typeLabel = srTypeOptions.find(opt => opt.value === sr.type)?.label || sr.type;
              const isActive = isResourceActiveInStressTest(sr.type);
              const config = RESOURCE_TYPE_CONFIG[sr.type] ?? RESOURCE_TYPE_CONFIG.other;

              // Card container with left accent border
              const cardClasses = `relative rounded-lg p-3 transition-colors ${
                isStressTestMode && !isActive
                  ? 'bg-red-50/50 dark:bg-red-900/10 border border-red-300 dark:border-red-700'
                  : 'bg-gray-50 dark:bg-primary-700/50 border border-gray-100 dark:border-primary-600'
              }`;

              // PV Anlage card
              if (sr.type === 'pv_anlage') {
                return (
                  <div key={sr.id} className={cardClasses}>
                    <div className={`absolute left-0 top-2 bottom-2 w-1 rounded-full ${config.bg}`} />
                    <div className="pl-2">
                      {isEditing ? (
                        <div className="space-y-3">
                          {renderCardHeader(sr, index, typeLabel, isActive)}
                          {renderEditFields(sr, index)}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {renderCardHeader(sr, index, typeLabel, isActive)}
                          <PvGenerationDisplay
                            pvAnlage={sr}
                            hausanschlussId={elementId}
                            onUpdate={(updatedPv) => updateSR(index, updatedPv, false).catch(console.error)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              // Other resource types
              const specs = buildSpecsLine(sr);
              return (
                <div key={sr.id} className={cardClasses}>
                  <div className={`absolute left-0 top-2 bottom-2 w-1 rounded-full ${config.bg}`} />
                  <div className="pl-2">
                    {isEditing ? (
                      <div className="space-y-3">
                        {renderCardHeader(sr, index, typeLabel, isActive)}
                        {renderEditFields(sr, index)}
                      </div>
                    ) : (
                      <>
                        {renderCardHeader(sr, index, typeLabel, isActive)}
                        {specs.length > 0 && (
                          <div className="ml-11 mt-0.5">
                            <span className="text-[12px] text-gray-500 dark:text-primary-300">
                              {specs.join('  \u00B7  ')}
                            </span>
                          </div>
                        )}
                        {(sr.trId || sr.srId || sr.neloId || sr.ratedPowerW != null || sr.deviceMinPowerW != null || sr.commissionedAt) && (
                          <div className="ml-11 mt-1 space-y-0.5">
                            <Fact label="TR-ID" value={sr.trId} mono />
                            <Fact label="SR-ID" value={sr.srId} mono />
                            <Fact label="NeLo-ID" value={sr.neloId} mono />
                            <Fact label="Nennleistung (Stammdaten)" value={formatWattAsKw(sr.ratedPowerW)} />
                            <Fact label="Mindestleistung Anlage" value={formatWattAsKw(sr.deviceMinPowerW)} />
                            <Fact label="Beginn" value={formatCalendarDate(sr.commissionedAt)} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!readOnly && (
            <button
              onClick={(e) => {
                e.preventDefault();
                addTR().catch(console.error);
              }}
              className="w-full text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/10 dark:hover:bg-emerald-500/20 border border-emerald-300 dark:border-emerald-700 h-7 flex items-center justify-center gap-1 rounded-md text-xs"
              title="TR hinzufügen"
            >
              <PlusIcon />
              <span>TR hinzufügen</span>
            </button>
          )}
          {readOnly && technischeRessourcen.length === 0 && (
            <div className="text-xs text-gray-400 dark:text-primary-400 px-1 py-1">Keine technischen Ressourcen hinterlegt.</div>
          )}
        </div>
      )}
    </div>
  );
};
